import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// ─── OAuth ───────────────────────────────────────────────
export const fetchLinkedInOAuthUrl = createAsyncThunk(
  'linkedinAds/fetchOAuthUrl',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/linkedin/oauth/url');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to get OAuth URL');
    }
  }
);

export const fetchLinkedInConnectionStatus = createAsyncThunk(
  'linkedinAds/fetchConnectionStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/linkedin/oauth/status');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch connection status');
    }
  }
);

export const disconnectLinkedIn = createAsyncThunk(
  'linkedinAds/disconnectLinkedIn',
  async (_, { rejectWithValue }) => {
    try {
      await api.delete('/marketing/linkedin/oauth/disconnect');
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to disconnect');
    }
  }
);

// ─── Ad Accounts ─────────────────────────────────────────
export const fetchLinkedInAdAccounts = createAsyncThunk(
  'linkedinAds/fetchAdAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/linkedin/accounts');
      return response.data.accounts || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch ad accounts');
    }
  }
);

// ─── Campaigns ───────────────────────────────────────────
export const fetchLinkedInCampaigns = createAsyncThunk(
  'linkedinAds/fetchCampaigns',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/linkedin/campaigns', { params });
      return response.data.campaigns || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch campaigns');
    }
  }
);

export const createLinkedInCampaign = createAsyncThunk(
  'linkedinAds/createCampaign',
  async ({ adAccountUrn, campaignData }, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/linkedin/campaigns', {
        adAccountUrn,
        campaign: campaignData,
      });
      return response.data.campaign || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create campaign');
    }
  }
);

export const updateLinkedInCampaign = createAsyncThunk(
  'linkedinAds/updateCampaign',
  async ({ campaignId, name, budget, status }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/marketing/linkedin/campaigns/${campaignId}`, {
        name,
        budget,
        status,
      });
      return response.data.campaign || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update campaign');
    }
  }
);

export const deleteLinkedInCampaign = createAsyncThunk(
  'linkedinAds/deleteCampaign',
  async ({ campaignId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/marketing/linkedin/campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete campaign');
    }
  }
);

export const syncLinkedInCampaignMetrics = createAsyncThunk(
  'linkedinAds/syncMetrics',
  async ({ campaignId }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/marketing/linkedin/campaigns/${campaignId}/sync`,
        {}
      );
      return response.data.campaign || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to sync metrics');
    }
  }
);

const initialState = {
  connected: false,
  linkedinUserId: null,
  linkedinEmail: null,
  linkedinName: null,
  organizationAccounts: [],
  campaigns: [],
  selectedAccount: null,

  loading: false,
  error: null,
};

const linkedinAdsSlice = createSlice({
  name: 'linkedinAds',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    selectAdAccount: (state, action) => {
      state.selectedAccount = action.payload;
    },
  },
  extraReducers: (builder) => {
    // OAuth URL
    builder
      .addCase(fetchLinkedInOAuthUrl.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLinkedInOAuthUrl.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.url) {
          window.location.href = action.payload.url;
        }
      })
      .addCase(fetchLinkedInOAuthUrl.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Connection Status
    builder
      .addCase(fetchLinkedInConnectionStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLinkedInConnectionStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.connected = action.payload.connected;
        state.linkedinUserId = action.payload.linkedinUserId;
        state.linkedinEmail = action.payload.linkedinEmail;
        state.linkedinName = action.payload.linkedinName;
        state.organizationAccounts = action.payload.organizationAccounts || [];
      })
      .addCase(fetchLinkedInConnectionStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.connected = false;
      });

    // Disconnect
    builder
      .addCase(disconnectLinkedIn.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(disconnectLinkedIn.fulfilled, (state) => {
        state.loading = false;
        state.connected = false;
        state.linkedinUserId = null;
        state.linkedinEmail = null;
        state.linkedinName = null;
        state.organizationAccounts = [];
        state.campaigns = [];
      })
      .addCase(disconnectLinkedIn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Ad Accounts
    builder
      .addCase(fetchLinkedInAdAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLinkedInAdAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.organizationAccounts = action.payload;
      })
      .addCase(fetchLinkedInAdAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Campaigns
    builder
      .addCase(fetchLinkedInCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLinkedInCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload;
      })
      .addCase(fetchLinkedInCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create Campaign
    builder
      .addCase(createLinkedInCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLinkedInCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns.push(action.payload);
      })
      .addCase(createLinkedInCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update Campaign
    builder
      .addCase(updateLinkedInCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLinkedInCampaign.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.campaigns.findIndex(
          (c) => c._id === action.payload._id
        );
        if (index !== -1) {
          state.campaigns[index] = action.payload;
        }
      })
      .addCase(updateLinkedInCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete Campaign
    builder
      .addCase(deleteLinkedInCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLinkedInCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = state.campaigns.filter(
          (c) => c._id !== action.meta.arg.campaignId
        );
      })
      .addCase(deleteLinkedInCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Sync Metrics
    builder
      .addCase(syncLinkedInCampaignMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(syncLinkedInCampaignMetrics.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.campaigns.findIndex(
          (c) => c._id === action.payload._id
        );
        if (index !== -1) {
          state.campaigns[index] = action.payload;
        }
      })
      .addCase(syncLinkedInCampaignMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, selectAdAccount } = linkedinAdsSlice.actions;
export default linkedinAdsSlice.reducer;
